/**
 * Event entity model class
 *
 * This file is part of Minasa
 * (c) Kaden und Partner AG
 */
import BaseModel from './BaseModel';
import BaseCollection from './BaseCollection';
import dayjs from 'dayjs';
import { frontendUrl, globalParam } from '@/helpers/globals';
import { instance as api } from '@/helpers/api';
import { User } from './User';
import { fieldRuleValue } from '@/helpers/tools';
import { userIsStaff } from '@/helpers/auth';

export type EventMetadata = {
    legalAcceptances: [{ acceptanceType: string; user: string; timestamp: string }];
};

export enum EventStatus {
    // eslint-disable-next-line
    DRAFT = 'draft',
    // eslint-disable-next-line
    REGISTERED = 'registered',
    // eslint-disable-next-line
    REJECTED = 'rejected',
    // eslint-disable-next-line
    RELEASED = 'released',
    // eslint-disable-next-line
    DELETED = 'deleted',
    // eslint-disable-next-line
    IMPORTED = 'imported',
}

export class Event extends BaseModel {
    static STATUS_DRAFT = EventStatus.DRAFT;
    static STATUS_REGISTERED = EventStatus.REGISTERED;
    static STATUS_REJECTED = EventStatus.REJECTED;
    static STATUS_RELEASED = EventStatus.RELEASED;
    static STATUS_DELETED = EventStatus.DELETED;
    static STATUS_IMPORTED = EventStatus.IMPORTED;

    static CLASS_EVENT = 'event';
    static CLASS_EXHIBITION = 'exhibition';
    static CLASS_CINEMA = 'cinema';
    static CLASS_FESTIVAL = 'festival';
    static CLASS_TOURNEE = 'tournee';
    static CLASS_PERMANENT_EXHIBITION = 'permanent_exhibition';
    static CLASS_STREAM = 'stream';

    static CLASSES_WITH_SCHEDULES = [
        this.CLASS_EVENT,
        this.CLASS_EXHIBITION,
        this.CLASS_CINEMA,
        // @see #32213: Tournee has no termine anymore (July 2026)
        // this.CLASS_TOURNEE,
    ];
    static CLASSES_WITH_SUB_EVENTS() {
        return [
            this.CLASS_FESTIVAL,
            this.CLASS_EXHIBITION,
            this.CLASS_PERMANENT_EXHIBITION,
            userIsStaff() ? this.CLASS_TOURNEE : false,
        ].filter(Boolean);
    }

    static MODE_MIXED = 'mixed';
    static MODE_ONLINE = 'online';
    static MODE_OFFLINE = 'offline';

    static GUIDLE_IMPORT_TYPE_SMALL = 'small';
    static GUIDLE_IMPORT_TYPE_LARGE = 'large';

    static LEAD_MAXLENGTH = 110; // defines the max number of chars in the Lead text

    // View props that will not be saved:
    transientProps(): string[] {
        return [
            'event_organizer',

            'is_published',
            'event_organizer',
            'location_uuid',
            'location_title',
            'location_address',
            'location_zip',
            'location_city',
            'location_phone',
            'location_mobile',
            'location_email',
            'location_homepage',
            'location_opening_hours',
            'location_country',
            'organizer_uuid',
            'organizer_address',
            'organizer_zip',
            'organizer_po_box',
            'organizer_city',
            'organizer_tel',
            'organizer_mobile',
            'organizer_email',
            'organizer_web',
            'organizer_group_id',
            'organizer_country',
            'organizer_ticketprices',
            'first_schedule_date',
            'last_schedule_date',
            'next_schedule_date',
            'organizer_n_location',
            'event_genre_groups',
            'genre_group_count',
            'main_genre_group',
            'main_genre_group_slug',
            'eventfrog_rubric',
            'tag_names',
            'schedule_tags',
            'creator',
            'modifier',
            'nutzungsrecht_zustimmung_user',
            'nutzungsrecht_zustimmung_datetime',
            'target_audience',
        ];
    }

    defaults() {
        return {
            // entity properties
            id: null,
            uuid: null,
            title: null,
            socialmedia_title: null,
            summary_text: null,
            description: null,
            slug: null,
            page_id: 1, // is always agenda (1: Agenda, 2: Magazin, 3: Service, 4: Kulturplatz, 5: Freie Seiten)
            ticketprices: null,
            created: null,
            modified: null,
            creator_id: null,
            modifier_id: null,
            location_id: null,
            status: Event.STATUS_DRAFT,
            rejected_reason: null,

            featured_image_id: null,
            featured_image_caption: null,
            featured_img_crop_config: null,
            featured_image_credits: null,

            bp_import_id: null,
            bp_import_date: null,
            bp_import_type: null,
            bp_import_origin: null,
            is_foreign_import: null,
            import_ident: null,
            organizer_id: null,
            to_newsletter: null,
            main_genre_group_id: null,
            standard_text_tickets: null,
            event_class: null,
            is_featured: null,
            for_print: null,
            published_date: null,
            opening_hours: null,
            eventfrog_rubric_id: null,
            import_protection: null,
            is_unique: null,
            original_event_url: null,
            online_event_url: null,
            event_mode: null,
            accessibility: null,
            accessibility_link: null,
            abstract: null,
            presales_link: null,
            target_audience_id: null,
            typical_age_range: null,
            external_links: null,
            documents: null,
            quality: null,
            quality_checked_at: null,
            edited: null, // redigiert
            for_website: null,

            // view properties
            is_published: null,
            event_organizer: null,
            page_title: null,

            location_uuid: null,
            location_title: null,
            location_address: null,
            location_zip: null,
            location_city: null,
            location_phone: null,
            location_mobile: null,
            location_email: null,
            location_homepage: null,
            location_opening_hours: null,
            location_country: null,
            location_po_box: null,
            location_description: null,

            organizer_uuid: null,
            organizer_legal_type: null,
            organizer_address: null,
            organizer_zip: null,
            organizer_po_box: null,
            organizer_city: null,
            organizer_tel: null,
            organizer_mobile: null,
            organizer_email: null,
            organizer_web: null,
            organizer_group_id: null,
            organizer_country: null,
            organizer_ticketprices: null,

            first_schedule_date: null,
            last_schedule_date: null,
            next_schedule_date: null,

            organizer_n_location: null,

            ak_durchgefuert: false,
            ak_bemerkung: '',
            ak_ausbezahlte_gage: 0,

            genre_groups: null,
            event_genre_groups: null,
            genre_group_count: null,
            main_genre_group: null,
            main_genre_group_slug: null,
            eventfrog_rubric: null,

            tags: null,
            tag_names: null,
            schedule_tags: null,

            parent_event_ids: null,
            child_event_ids: null,

            creator: null,
            modifier: null,

            nutzungsrecht_zustimmung_user: null,
            nutzungsrecht_zustimmung_datetime: null,

            target_audience: null,
        };
    }

    get next_schedule_date_display() {
        return this.next_schedule_date.formatIf('dd, DD.MM.YYYY', '-');
    }

    get isPublished() {
        return this.status === Event.STATUS_RELEASED;
    }

    get locationTitleWithCity() {
        return [this.location_title || null, this.location_city || null].filter(Boolean).join(', ') || '';
    }

    get status_icon() {
        return getEventStatusIconName(this.status);
    }

    get calculatedEventMode() {
        if (this.online_event_url && this.location_id) {
            return Event.MODE_MIXED;
        }
        if (this.online_event_url && !this.location_id) {
            return Event.MODE_ONLINE;
        }
        return Event.MODE_OFFLINE;
    }

    get status_icon_color() {
        return getEventStatusIconColor(this.status);
    }

    get canEditTitle() {
        return this.canEditField('title');
    }

    get canEditGenreGroups() {
        return this.canEditField('genre_groups');
    }

    get canEditMainGenreGroup() {
        return this.canEditField('main_genre_group_id');
    }
    get canEditAbstract() {
        return this.canEditField('abstract');
    }

    get canEditSocialMediaTitle() {
        return this.canEditField('socialmedia_title');
    }

    get canEditTargetAudience() {
        return this.canEditField('target_audience_id');
    }

    canEditField(fieldName: string): boolean {
        // staff-members can always edit:
        if (userIsStaff()) {
            return true;
        }

        // Some fields are protected for non-staff users after publishing the event. This can be set by enabling the
        // rule in env.php:
        const protectionRuleActivated = fieldRuleValue(`field_rules.protect_after_publish.event.${fieldName}`, false);
        if (protectionRuleActivated) {
            return this.isPublished !== true;
        }
        return true;
    }

    mutations() {
        return {
            id: (id: any) => (Number(id) > 0 ? Number(id) : null),
            is_featured: Boolean,
            for_print: Boolean,
            for_website: Boolean,
            edited: Boolean,
            event_class: (event_class: any) => {
                return event_class ? event_class : Event.CLASS_EVENT;
            },
            ak_durchgefuert: Boolean,
            ak_ausbezahlte_gage: Number,
            created: dayjs,
            modified: dayjs,
            quality_checked_at: dayjs,
            bp_import_date: dayjs,
            first_schedule_date: dayjs,
            next_schedule_date: dayjs,
            last_schedule_date: dayjs,
            is_unique: Boolean,
        };
    }

    entityName() {
        return 'event';
    }

    get detailPreviewUrl() {
        const previewTemplate = globalParam('event.preview_url_template');
        if (previewTemplate) {
            return this.replaceUrlPlaceholders(previewTemplate);
        } else {
            return frontendUrl(`/agenda/${this.slug || this.id}`, { preview: 'enable' });
        }
    }

    get detailUrl() {
        const frontentUrlTpl = globalParam('event.frontend_url_template');
        if (frontentUrlTpl) {
            return this.replaceUrlPlaceholders(frontentUrlTpl);
        } else {
            return frontendUrl(`/agenda/${this.slug || this.id}`);
        }
    }

    get searchPreviewUrl() {
        const frontentUrlTpl = globalParam('event.frontend_search_url_template');
        if (frontentUrlTpl) {
            return this.replaceUrlPlaceholders(frontentUrlTpl);
        } else {
            return frontendUrl(`/agenda/?preview=enable&id=${this.id}#id=${this.id}`);
        }
    }

    protected replaceUrlPlaceholders(urlTpl: string) {
        const placeholderMap = {
            '{event_id}': this.getId() || '',
            '{event_slug}': this.slug,
        };
        for (const entry of Object.entries(placeholderMap)) {
            urlTpl = urlTpl.replace(entry[0], entry[1]);
        }
        return urlTpl;
    }

    userCanSave(user: User | null) {
        // staff members can always save:
        if (user?.isStaff) {
            return true;
        }

        // the event can only be saved when it is not submitted for checking:
        if (this.status !== Event.STATUS_REGISTERED) {
            return true;
        }
        return false;
    }

    async checkStatusChange(newStatus: EventStatus | null) {
        const res = await api().post('/Event/checkNextStatus', {
            event_id: this.id,
            status: newStatus,
        });
        return res.data?.reasons || false;
    }
}

export class EventCollection extends BaseCollection {
    options() {
        return {
            model: Event,
        };
    }

    entityName() {
        return 'event';
    }
}

export function getEventStatusIconName(status: EventStatus | null) {
    switch (status) {
        case Event.STATUS_DRAFT:
            return 'pencil';
        case Event.STATUS_IMPORTED:
            return 'new-from';
        case Event.STATUS_REGISTERED:
            return 'hourglass';
        case Event.STATUS_REJECTED:
            return 'rejected';
        case Event.STATUS_RELEASED:
            return 'like';
        case Event.STATUS_DELETED:
            return 'trash';
    }
    return status;
}

export function getEventStatusIconColor(status: EventStatus | null) {
    switch (status) {
        case Event.STATUS_DRAFT:
            return 'status-draft';
        case Event.STATUS_IMPORTED:
            return 'status-imported';
        case Event.STATUS_REGISTERED:
            return 'status-registered';
        case Event.STATUS_REJECTED:
            return 'status-rejected';
        case Event.STATUS_RELEASED:
            return 'status-released';
        case Event.STATUS_DELETED:
            return 'primary';
    }
    return 'grey';
}
